import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import './SignupPopup.css';

const SignupPopup = ({ isOpen: externalIsOpen, onClose }) => {
  const [internalModal, setInternalModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    instagram: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' });

  // Determine if modal should be open based on props or internal state
  const isModalOpen = externalIsOpen !== undefined ? externalIsOpen : internalModal;

  // Show modal on component mount (when user first lands on the site)
  useEffect(() => {
    if (externalIsOpen === undefined) { // Only auto-show if not controlled by parent
      const hasSeenPopup = localStorage.getItem('hasSeenOnexPopup');
      if (!hasSeenPopup) {
        const timer = setTimeout(() => {
          setInternalModal(true);
          localStorage.setItem('hasSeenOnexPopup', 'true');
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [externalIsOpen]);

  const toggle = () => {
    if (onClose) {
      onClose();
    } else {
      setInternalModal(!internalModal);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitStatus({ success: false, message: 'Please fill in all required fields' });
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ success: false, message: 'Please enter a valid email address' });
      return;
    }
    
    // Validate phone number (10 digits)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      setSubmitStatus({ success: false, message: 'Please enter a valid 10-digit phone number' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare email content
      const subject = `New Signup: ${formData.name}`;
      const body = `Name: ${formData.name}%0D%0A` +
                  `Email: ${formData.email}%0D%0A` +
                  `Phone: ${formData.phone}%0D%0A` +
                  `Instagram: ${formData.instagram || 'Not provided'}`;
      
      // Create mailto link
      const mailtoLink = `mailto:info@onexinfinity.com?subject=${encodeURIComponent(subject)}&body=${body}`;
      
      // Open default email client
      window.location.href = mailtoLink;
      
      // Show success message
      setSubmitStatus({ 
        success: true, 
        message: 'Thank you for signing up! Your email client should open with a pre-filled message.' 
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        instagram: ''
      });
      
      // Close the modal after 3 seconds
      setTimeout(() => {
        if (onClose) {
          onClose();
        } else {
          setInternalModal(false);
        }
      }, 3000);
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus({ 
        success: false, 
        message: 'An error occurred. Please try again later.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isModalOpen} toggle={toggle} centered className="signup-modal">
      <ModalHeader toggle={toggle}>
        <h4>We're building something wild. You in or what?</h4>
      </ModalHeader>
      <ModalBody>
        {submitStatus.message && (
          <div className={`alert ${submitStatus.success ? 'alert-success' : 'alert-danger'}`}>
            {submitStatus.message}
          </div>
        )}
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="name">Full Name *</Label>
            <Input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label for="email">Email Address *</Label>
            <Input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label for="phone">Phone Number *</Label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">+91</span>
              </div>
              <Input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleChange}
                pattern="[0-9]{10}"
                placeholder="Enter 10-digit number"
                required
              />
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label for="instagram">Instagram Handle (Optional)</Label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">@</span>
              </div>
              <Input
                type="text"
                name="instagram"
                id="instagram"
                value={formData.instagram}
                onChange={handleChange}
              />
            </div>
          </FormGroup>
          
          <Button 
            color="primary" 
            block 
            className="mt-4"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Join Now'}
          </Button>
          
          <p className="text-muted small mt-3 mb-0">
            We'll never share your information with anyone else.
          </p>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default SignupPopup;
